/**
 * @fileoverview Drop zone for the two Meta Ads Manager exports (keeps the latest two files).
 * @module features/ad-spends/components/import/ImportFilesDropzone
 */

import { useCallback, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/shared/utils/cn'

const MAX_FILE_BYTES = 5 * 1024 * 1024
const REQUIRED_FILES = 2

export interface ImportFilesDropzoneProps {
  files: readonly File[]
  onFilesChange: (files: File[]) => void
  disabled?: boolean
}

export function ImportFilesDropzone({ files, onFilesChange, disabled = false }: ImportFilesDropzoneProps): ReactElement {
  const { t } = useTranslation('features-ad-spends')
  const [error, setError] = useState<string | null>(null)

  const onDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      if (rejections.length > 0) {
        setError(t('import.dropzone.invalidFile', { defaultValue: 'Only .csv files up to 5 MB are accepted (at most 2).' }))
        return
      }
      setError(null)
      // Files can be dropped one at a time: keep the most recent two.
      onFilesChange([...files, ...accepted].slice(-REQUIRED_FILES))
    },
    [files, onFilesChange, t]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.csv'] },
    multiple: true,
    maxFiles: REQUIRED_FILES,
    maxSize: MAX_FILE_BYTES,
    disabled,
  })

  return (
    <div className="space-y-3">
      <div
        {...getRootProps({
          role: 'button',
          'aria-label': t('import.dropzone.aria', { defaultValue: 'Drop Meta export files' }),
        })}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition-colors',
          isDragActive ? 'border-primary/60 bg-accent/30' : 'border-border hover:border-primary/40',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <input {...getInputProps({ 'aria-label': t('import.dropzone.input', { defaultValue: 'Meta export files' }) })} />
        <Upload className="mb-2 size-8 text-muted-foreground" />
        <p className="text-sm font-medium">{t('import.dropzone.title', { defaultValue: 'Drop both Meta exports here' })}</p>
        <p className="text-xs text-muted-foreground">
          {t('import.dropzone.subtitle', { defaultValue: 'Product ID export + Country export (.csv, max 5 MB each)' })}
        </p>
      </div>

      {files.length > 0 && (
        <div className="flex items-start justify-between gap-2">
          <ul className="min-w-0 flex-1 space-y-1 text-sm">
            {files.map(file => (
              <li key={`${file.name}-${file.size}`} className="flex justify-between gap-2 rounded-md bg-accent/40 px-2 py-1">
                <span className="truncate">{file.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</span>
              </li>
            ))}
          </ul>
          <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => onFilesChange([])}>
            {t('import.dropzone.clear', { defaultValue: 'Clear' })}
          </Button>
        </div>
      )}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}
